/**
 * One search across everything the world is made of.
 *
 * The Create window keeps items, characters, scenery and quests behind tabs of
 * their own, which is right for writing one and wrong for finding one: nobody
 * looking for the Copper Hatchet wants to first remember whether a hatchet is
 * an item or a piece of scenery.
 *
 * Everything here comes out of the definitions the client already holds, so it
 * costs nothing and cannot be out of date with the world it is searching.
 */

import { useMemo, useState } from 'react';
import { findIn, KIND_LABELS, type Found } from './find';

export function EditorFind({
  world,
  onOpen,
  onClose,
}: {
  world: Parameters<typeof findIn>[1];
  /** Opens whichever editor holds the thing found, on that thing. */
  onOpen: (found: Found) => void;
  onClose: () => void;
}): JSX.Element {
  const [said, setSaid] = useState('');
  const found = useMemo(() => findIn(said, world), [said, world]);

  return (
    <div className="editor-find">
      <input
        className="editor-search"
        type="text"
        autoFocus
        placeholder="Find anything: copper, 102, ardor"
        value={said}
        onChange={(event) => setSaid(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();

          // Enter takes the first one, since typing three letters and pressing
          // Enter is how anybody who knows what they are looking for searches.
          if (event.key === 'Enter' && found[0]) onOpen(found[0]);
        }}
      />

      {said.trim().length > 0 && found.length === 0 ? (
        <p className="hint">Nothing by that name.</p>
      ) : null}

      <div className="editor-find-list">
        {found.map((one) => (
          <button
            key={`${one.kind}-${one.id}`}
            type="button"
            className="editor-row"
            onClick={() => onOpen(one)}
          >
            <span className="editor-find-kind">{KIND_LABELS[one.kind]}</span>
            <span>{one.name}</span>
            <span className="editor-row-id">{one.note}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
